import { Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Cap } from '../../primitives'

/** Who else is in the account, and what they are allowed to do. */
const PEOPLE = [
  { email: 'tobenna@cai.ai', role: 'Admin' },
  { email: 'adam@cai.ai', role: 'Safety' },
  { email: 'bob@cai.ai', role: 'Fleet owner' },
]

const TOOLS = [
  { name: 'Samsara', on: true },
  { name: 'QuickBooks', on: true },
  { name: 'Bank accounts', on: true },
  { name: 'Gmail', on: false },
]

interface GroupProps {
  label: string
  /** Ruled off from the group above it. The first one has nothing to divide from. */
  divided?: boolean
  /** What adding to this group is called, for the + beside its heading. */
  addLabel?: string
  onAdd?: () => void
  children: React.ReactNode
}

function Group({ label, divided, addLabel, onAdd, children }: GroupProps) {
  return (
    <div className={cn('flex flex-col gap-2', divided && 'border-ink-100 border-t pt-4')}>
      <div className="flex items-center justify-between gap-2">
        <Cap>{label}</Cap>
        {addLabel && (
          <Button
            size="xs"
            variant="iconOnly"
            aria-label={addLabel}
            onClick={onAdd}
            className="text-ink-400 hover:text-ink"
          >
            <Plus className="size-3.5" />
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  )
}

export function ConnectTab({ onInvite, contentWidth }: { onInvite?: () => void; contentWidth?: number }) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div
        className="mx-auto flex w-full flex-col gap-4 p-3"
        style={contentWidth ? { maxWidth: contentWidth } : undefined}
      >
        <h2 className="text-ink text-sm font-bold tracking-tight">Connect</h2>
        <Group label="People" addLabel="Invite someone" onAdd={onInvite}>
          {PEOPLE.map((person) => (
            <div key={person.email} className="flex items-center justify-between gap-2">
              <span className="text-ink truncate text-[11px]">{person.email}</span>
              <span className="bg-ink-50 text-ink-500 shrink-0 rounded-full px-2 py-0.5 text-[10px]">
                {person.role}
              </span>
            </div>
          ))}
        </Group>

        <Group label="Tools" divided addLabel="Connect a tool">
          {TOOLS.map((tool) => (
            <div key={tool.name} className="flex items-center justify-between gap-2">
              <span className="text-ink truncate text-xs font-medium">{tool.name}</span>
              {/* Disconnecting is the quieter of the two: it undoes rather than adds. */}
              <Button size="xs" variant={tool.on ? 'outline' : 'default'} className="shrink-0">
                {tool.on ? 'Disconnect' : 'Connect'}
              </Button>
            </div>
          ))}
        </Group>
      </div>
    </div>
  )
}
